import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSpring, animated } from '@react-spring/web';
import { toast } from 'react-toastify';
import RoundedSelect from '../../components/Forms/RoundedSelect/RoundedSelect';
import {
  submitCapitecRiseSignup,
  hasCapitecRiseSubmitted,
} from '../../utils/capitecRiseSignup';
import './CapitecRiseApplyModal.css';

const EMPTY_FORM = {
  fullName: '',
  email: '',
  phone: '',
  age: '',
  city: '',
  tiktok: '',
  instagram: '',
  youtube: '',
  contentDuration: '',
  employmentStatus: '',
  capitecClient: '',
};

const CAPITEC_CLIENT_OPTIONS = [
  { value: '', label: 'Are you currently a Capitec client?', secondary: true },
  { value: 'yes', label: 'Yes, I have a Capitec account' },
  { value: 'no', label: 'No, not yet' },
];

const AGE_OPTIONS = [
  { value: '', label: 'Select age range', secondary: true },
  { value: '18-24', label: '18–24' },
  { value: '25-34', label: '25–34' },
  { value: '35-44', label: '35–44' },
  { value: '45-plus', label: '45+' },
];

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: '', label: 'Select employment status', secondary: true },
  { value: 'university-student', label: 'University Student' },
  { value: 'unemployed', label: 'Unemployed' },
  { value: 'employed-part-time', label: 'Employed Part-Time' },
  { value: 'employed-full-time', label: 'Employed Full-Time' },
  { value: 'self-employed', label: 'Self-Employed' },
];

const CONTENT_DURATION_OPTIONS = [
  { value: '', label: 'Select an option', secondary: true },
  { value: 'less-than-6-months', label: 'Less than 6 months' },
  { value: '6-months-to-1-year', label: '6 months to 1 year' },
  { value: '1-2-years', label: '1–2 years' },
  { value: '3-5-years', label: '3–5 years' },
  { value: '5-plus-years', label: '5+ years' },
];

const Field = ({ label, required, error, hint, children }) => (
  <label className={`capitec-rise-apply-field${error ? ' has-error' : ''}`}>
    <span className="capitec-rise-apply-label">
      {label}
      {required ? <span className="capitec-rise-apply-required"> *</span> : null}
    </span>
    {children}
    {error ? <span className="capitec-rise-apply-error">{error}</span> : null}
    {!error && hint ? (
      <span className="capitec-rise-apply-hint">{hint}</span>
    ) : null}
  </label>
);

const SelectField = ({ error, children }) => (
  <div className={`capitec-rise-apply-select-field${error ? ' has-error' : ''}`}>
    {children}
    {error ? <span className="capitec-rise-apply-error">{error}</span> : null}
  </div>
);

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const SOCIAL_HINT =
  'You must enter at least one Active TikTok, Instagram or YouTube account';

const SOCIAL_REQUIRED_ERROR =
  'Enter a TikTok, Instagram or YouTube handle';

const validateForm = form => {
  const errors = {};

  if (!form.fullName.trim()) {
    errors.fullName = 'Full name is required';
  }

  if (!form.email.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_PATTERN.test(form.email.trim())) {
    errors.email = 'Enter a valid email address';
  }

  if (!form.phone.trim()) {
    errors.phone = 'Phone number is required';
  }

  if (!form.age) {
    errors.age = 'Please select an age range';
  }

  if (!form.city.trim()) {
    errors.city = 'City is required';
  }

  if (!form.tiktok.trim() && !form.instagram.trim() && !form.youtube.trim()) {
    errors.tiktok = SOCIAL_REQUIRED_ERROR;
    errors.instagram = SOCIAL_REQUIRED_ERROR;
    errors.youtube = SOCIAL_REQUIRED_ERROR;
  } else {
    if (form.tiktok.trim() && !form.tiktok.trim().startsWith('@')) {
      errors.tiktok = 'Handle must start with @';
    }
    if (form.instagram.trim() && !form.instagram.trim().startsWith('@')) {
      errors.instagram = 'Handle must start with @';
    }
    if (form.youtube.trim() && !form.youtube.trim().startsWith('@')) {
      errors.youtube = 'Handle must start with @';
    }
  }

  if (!form.contentDuration) {
    errors.contentDuration = 'Please select an option';
  }

  if (!form.employmentStatus) {
    errors.employmentStatus = 'Please select your employment status';
  }

  if (!form.capitecClient) {
    errors.capitecClient = 'Please select an option';
  }

  return errors;
};

const OPTION_SETS = {
  age: AGE_OPTIONS,
  contentDuration: CONTENT_DURATION_OPTIONS,
  employmentStatus: EMPLOYMENT_STATUS_OPTIONS,
};

const CapitecRiseApplyModal = ({ isOpen, onClose, onSubmitted, onSuccess }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [honeypot, setHoneypot] = useState('');
  const [useOverlayDropdown, setUseOverlayDropdown] = useState(true);
  const formOpenedAtRef = useRef(null);
  const reduced =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const overlayAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    config: { tension: 90, friction: 26, mass: 1.2 },
  });

  const cardAnimation = useSpring({
    opacity: isOpen ? 1 : 0,
    transform: isOpen ? 'scale(1)' : 'scale(0.8)',
    config: { tension: 90, friction: 18, mass: 1.5 },
    immediate: reduced,
  });

  useEffect(() => {
    const media = window.matchMedia('(max-width: 768px)');
    const update = () => setUseOverlayDropdown(!media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleKeyDown = event => {
      if (event.key === 'Escape' && !submitting) onClose();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose, submitting]);

  useEffect(() => {
    if (!isOpen) {
      setErrors({});
      setForm(EMPTY_FORM);
      setHoneypot('');
      formOpenedAtRef.current = null;
      return;
    }

    formOpenedAtRef.current = Date.now();
  }, [isOpen]);

  const updateField = field => event => {
    const value = event.target.value;
    setForm(current => ({ ...current, [field]: value }));
    setErrors(current => {
      const next = { ...current };
      delete next[field];
      if (field === 'tiktok' || field === 'instagram' || field === 'youtube') {
        delete next.tiktok;
        delete next.instagram;
        delete next.youtube;
      }
      return next;
    });
  };

  const updateSelect = field => value => {
    setForm(current => ({ ...current, [field]: value }));
    setErrors(current => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const showSubmitError = message => {
    setErrors(current => ({ ...current, form: message }));
    toast.error(message);
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (submitting) return;

    const nextErrors = validateForm(form);
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setErrors(current => {
      if (!current.form) return current;
      const next = { ...current };
      delete next.form;
      return next;
    });
    setSubmitting(true);

    try {
      if (hasCapitecRiseSubmitted()) {
        showSubmitError('You have already submitted an application.');
        return;
      }

      const result = await submitCapitecRiseSignup(form, OPTION_SETS, {
        honeypot,
        formOpenedAt: formOpenedAtRef.current,
      });
      if (result?.suppressed) {
        showSubmitError('Unable to submit your application. Please try again.');
        return;
      }

      toast.success('Thank you! Your application has been submitted.');
      onSubmitted?.();
      onSuccess?.();
    } catch (error) {
      if (error.code === 'already_submitted') {
        showSubmitError('You have already submitted an application.');
      } else if (error.code === 'too_fast') {
        showSubmitError('Please wait a moment before submitting.');
      } else if (error.code === 'invalid') {
        showSubmitError('Unable to submit your application. Please try again.');
      } else {
        showSubmitError(
          'There was a problem submitting your application. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <animated.div
      className="capitec-rise-apply-overlay"
      style={overlayAnimation}
      onClick={() => {
        if (!submitting) onClose();
      }}
      role="presentation"
    >
      <animated.div
        className="capitec-rise-apply-modal"
        style={cardAnimation}
        role="dialog"
        aria-modal="true"
        aria-labelledby="capitec-rise-apply-title"
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          className="capitec-rise-apply-close"
          onClick={onClose}
          disabled={submitting}
          aria-label="Close apply form"
        >
          ×
        </button>

        <h2 id="capitec-rise-apply-title" className="capitec-rise-apply-title">
          Capitec rise program application
        </h2>
        <p className="capitec-rise-apply-subtitle">
          Please complete and submit the below to complete your application.
        </p>

        <form className="capitec-rise-apply-form" onSubmit={handleSubmit} noValidate>
            <input
              type="text"
              name="cr_hp_field"
              value={honeypot}
              onChange={event => setHoneypot(event.target.value)}
              tabIndex={-1}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-lpignore="true"
              data-1p-ignore="true"
              data-form-type="other"
              aria-hidden="true"
              className="capitec-rise-apply-honeypot"
            />
            <div className="capitec-rise-apply-grid">
              <Field label="Full Name" required error={errors.fullName}>
                <input
                  type="text"
                  name="fullName"
                  autoComplete="name"
                  value={form.fullName}
                  onChange={updateField('fullName')}
                  placeholder="e.g. Jane Smith"
                  aria-invalid={Boolean(errors.fullName)}
                />
              </Field>
              <Field label="Email" required error={errors.email}>
                <input
                  type="email"
                  name="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={updateField('email')}
                  placeholder="e.g. jane@email.com"
                  aria-invalid={Boolean(errors.email)}
                />
              </Field>
              <Field label="Phone Number" required error={errors.phone}>
                <input
                  type="tel"
                  name="phone"
                  autoComplete="tel"
                  value={form.phone}
                  onChange={updateField('phone')}
                  placeholder="e.g. 082 123 4567"
                  aria-invalid={Boolean(errors.phone)}
                />
              </Field>
              <SelectField error={errors.age}>
                <RoundedSelect
                  variant="capitec"
                  overlayDropdown={useOverlayDropdown}
                  label="Age"
                  required
                  placeholder="Select age range"
                  value={form.age}
                  options={AGE_OPTIONS}
                  onChange={updateSelect('age')}
                />
              </SelectField>
              <Field label="City" required error={errors.city}>
                <input
                  type="text"
                  name="city"
                  autoComplete="address-level2"
                  value={form.city}
                  onChange={updateField('city')}
                  placeholder="e.g. Johannesburg"
                  aria-invalid={Boolean(errors.city)}
                />
              </Field>
              <SelectField error={errors.contentDuration}>
                <RoundedSelect
                  variant="capitec"
                  overlayDropdown={useOverlayDropdown}
                  label="How long have you been creating content for?"
                  required
                  placeholder="Select an option"
                  value={form.contentDuration}
                  options={CONTENT_DURATION_OPTIONS}
                  onChange={updateSelect('contentDuration')}
                />
              </SelectField>
              <SelectField error={errors.employmentStatus}>
                <RoundedSelect
                  variant="capitec"
                  overlayDropdown={useOverlayDropdown}
                  label="Current employment status"
                  required
                  placeholder="Select employment status"
                  value={form.employmentStatus}
                  options={EMPLOYMENT_STATUS_OPTIONS}
                  onChange={updateSelect('employmentStatus')}
                />
              </SelectField>
              <SelectField error={errors.capitecClient}>
                <RoundedSelect
                  variant="capitec"
                  overlayDropdown={useOverlayDropdown}
                  label="Are you currently a Capitec client"
                  required
                  placeholder="Are you currently a Capitec client?"
                  value={form.capitecClient}
                  options={CAPITEC_CLIENT_OPTIONS}
                  onChange={updateSelect('capitecClient')}
                />
              </SelectField>
              <p className="capitec-rise-apply-hint">
                <span className="capitec-rise-apply-required">*</span> {SOCIAL_HINT}
              </p>
              <Field label="TikTok handle" error={errors.tiktok}>
                <input
                  type="text"
                  name="tiktokHandle"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  value={form.tiktok}
                  onChange={updateField('tiktok')}
                  placeholder="e.g. @janesmith"
                  aria-invalid={Boolean(errors.tiktok)}
                />
              </Field>
              <Field label="Instagram handle" error={errors.instagram}>
                <input
                  type="text"
                  name="instagramHandle"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  value={form.instagram}
                  onChange={updateField('instagram')}
                  placeholder="e.g. @janesmith"
                  aria-invalid={Boolean(errors.instagram)}
                />
              </Field>
              <Field label="YouTube handle" error={errors.youtube}>
                <input
                  type="text"
                  name="youtubeHandle"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck="false"
                  data-lpignore="true"
                  data-1p-ignore="true"
                  data-form-type="other"
                  value={form.youtube}
                  onChange={updateField('youtube')}
                  placeholder="e.g. @janesmith"
                  aria-invalid={Boolean(errors.youtube)}
                />
              </Field>
            </div>
            {errors.form ? (
              <p className="capitec-rise-apply-form-error" role="alert">
                {errors.form}
              </p>
            ) : null}
            <button
              className="capitec-rise-btn capitec-rise-btn--primary capitec-rise-apply-submit"
              type="submit"
              disabled={submitting}
            >
              {submitting ? (
                <span className="capitec-rise-apply-loader">
                  <svg
                    className="capitec-rise-apply-spinner"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeOpacity="0.25"
                    />
                    <path
                      d="M21 12a9 9 0 0 0-9-9"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                    />
                  </svg>
                  Sending...
                </span>
              ) : (
                'Submit'
              )}
            </button>
          </form>
        {submitting ? (
          <div className="capitec-rise-apply-loading" role="status" aria-live="polite">
            <span className="capitec-rise-apply-loader">
              <svg
                className="capitec-rise-apply-spinner"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeOpacity="0.25"
                />
                <path
                  d="M21 12a9 9 0 0 0-9-9"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              Submitting...
            </span>
          </div>
        ) : null}
      </animated.div>
    </animated.div>,
    document.body
  );
};

export default CapitecRiseApplyModal;
